import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import LanguageToggle from '../components/LanguageToggle';
import { toast } from 'sonner';
import { Eye, EyeSlash, Sparkle, ArrowRight, MapPin, ShareNetwork, Trophy } from '@phosphor-icons/react';

export default function Login() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';

  const handleShare = async () => {
    const shareData = {
      title: t('home.shareTitle'),
      text: t('home.shareText'),
      url: window.location.origin,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.origin);
        toast.success(t('home.linkCopied'));
      }
    } catch (_) {
      // user cancelled share - ignore
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Black brand panel with prominent logo */}
      <div className="hidden lg:flex lg:w-1/2 bg-black relative overflow-hidden">
        {/* Subtle decorative grid */}
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
          aria-hidden="true"
        />
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/[0.04] blur-3xl" aria-hidden="true" />
        <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-white/[0.04] blur-3xl" aria-hidden="true" />

        <div className="relative z-10 flex flex-col justify-center items-start p-16 text-white w-full">
          {/* Lang toggle - top right of dark panel */}
          <div className="absolute top-6 right-6">
            <LanguageToggle variant="dark" />
          </div>
          <img
            src="/logo-tk-dark.png"
            alt="Tia Karol Sports"
            className="w-64 h-64 object-contain mb-10 drop-shadow-2xl"
            data-testid="login-brand-logo"
          />
          <h1 className="font-['Outfit'] text-5xl font-bold leading-none tracking-tight">
            Tia Karol
            <br />
            <span className="text-white/70">Sports</span>
          </h1>
          <div className="mt-6 h-1 w-16 bg-white rounded-full" aria-hidden="true" />
          <p className="text-lg text-white/70 max-w-md leading-relaxed mt-6 font-light">
            {t('brand.tagline')}
          </p>
          <a
            href="https://maps.google.com/?q=Quiet+Waters+Park+401+S+Powerline+Rd+Deerfield+Beach+FL+33442"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-start gap-2 text-white/80 hover:text-white transition-colors group"
            data-testid="brand-address-desktop"
          >
            <MapPin size={20} weight="fill" className="text-[#FFB703] flex-shrink-0 mt-0.5" />
            <span className="text-sm leading-tight">
              <span className="font-semibold block">{t('brand.address')}</span>
              <span className="text-white/60 text-xs">{t('brand.addressFull')}</span>
            </span>
          </a>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#FDFBF7] relative">
        {/* Lang toggle (mobile + light-side desktop) */}
        <div className="absolute top-4 right-4 lg:hidden">
          <LanguageToggle />
        </div>
        <div className="w-full max-w-md">
          {/* Mobile Logo - prominent B&W */}
          <div className="lg:hidden flex flex-col items-center gap-3 mb-8">
            <div className="w-24 h-24 bg-black rounded-2xl flex items-center justify-center p-2 shadow-xl">
              <img
                src="/logo-tk-dark.png"
                alt="Tia Karol Sports"
                className="w-full h-full object-contain"
              />
            </div>
            <h1 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A] tracking-tight">Tia Karol Sports</h1>
          </div>

          {/* Torneio TK League ao vivo - acompanhamento público */}
          <Link
            to="/tk-league/torneio-publico"
            data-testid="live-tournament-link"
            className="block mb-4 group"
          >
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1A1A1A] via-[#2D2D2D] to-[#1A1A1A] p-4 text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 border border-[#FFB703]/30">
              <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-[#FFB703]/10 blur-2xl" aria-hidden="true" />
              <div className="relative flex items-center gap-3">
                <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-[#FFB703]/15 flex items-center justify-center">
                  <Trophy size={24} weight="fill" className="text-[#FFB703]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] uppercase tracking-wider text-[#FFB703] font-semibold flex items-center gap-1.5">
                    <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" /> Ao vivo
                  </p>
                  <p className="font-['Outfit'] text-base sm:text-lg font-bold leading-tight">
                    Torneio TK League
                  </p>
                  <p className="text-xs text-white/70 mt-0.5">Acompanhe resultados e chaves em tempo real</p>
                </div>
                <ArrowRight size={20} weight="bold" className="flex-shrink-0 transition-transform group-hover:translate-x-1" />
              </div>
            </div>
          </Link>

          {/* Promo Banner - Primeira Aula $15 */}
          <Link
            to="/aula-avulsa"
            data-testid="promo-banner-first-trial"
            className="block mb-6 group"
          >
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#E85D04] via-[#DC2F02] to-[#9B2226] p-5 text-white shadow-lg shadow-[#E85D04]/30 hover:shadow-xl hover:shadow-[#E85D04]/40 transition-all hover:-translate-y-0.5">
              <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/10 blur-2xl" aria-hidden="true" />
              <div className="absolute -bottom-6 -left-6 w-20 h-20 rounded-full bg-[#FFB703]/20 blur-xl" aria-hidden="true" />
              <div className="relative flex items-center gap-3">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
                  <Sparkle size={26} weight="fill" className="text-[#FFB703]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] uppercase tracking-wider text-white/80 font-semibold">{t('home.promoBadge')}</p>
                  <p className="font-['Outfit'] text-lg sm:text-xl font-bold leading-tight">
                    {t('home.promoTitle')} <span className="text-[#FFB703]">$15</span>
                  </p>
                  <p className="text-xs text-white/85 mt-0.5">{t('home.promoSubtitle')}</p>
                </div>
                <ArrowRight size={22} weight="bold" className="flex-shrink-0 transition-transform group-hover:translate-x-1" />
              </div>
            </div>
          </Link>

          <div className="bg-white border border-[#E6E2D8] rounded-xl p-8">
            <h2 className="font-['Outfit'] text-2xl font-semibold text-[#1A1A1A] mb-2">
              {t('login.title')}
            </h2>
            <p className="text-[#73706A] mb-8">
              {t('login.subtitle')}
            </p>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-[#9B2226] text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                  {t('common.email')}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field"
                  placeholder={t('login.emailPlaceholder')}
                  required
                  data-testid="login-email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                  {t('common.password')}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field pr-12"
                    placeholder="••••••••"
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[#73706A] hover:text-[#1A1A1A]"
                    data-testid="toggle-password-btn"
                  >
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <div className="mt-2 text-right">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-[#E85D04] hover:text-[#DC2F02] font-medium"
                    data-testid="forgot-password-link"
                  >
                    {t('login.forgotPassword')}
                  </Link>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="login-submit-btn"
              >
                {loading ? `${t('common.loading')}` : t('login.loginButton')}
              </button>
            </form>

            <p className="mt-6 text-center text-[#73706A]">
              {t('login.noAccount')}{' '}
              <Link to="/register" className="text-[#E85D04] hover:text-[#DC2F02] font-medium" data-testid="register-link">
                {t('login.createAccount')}
              </Link>
            </p>
            <p className="mt-3 text-center">
            </p>
            <div className="mt-4 pt-4 border-t border-[#E6E2D8] space-y-3">
              <a
                href="https://maps.google.com/?q=Quiet+Waters+Park+401+S+Powerline+Rd+Deerfield+Beach+FL+33442"
                target="_blank"
                rel="noopener noreferrer"
                className="lg:hidden flex items-start gap-2 px-3 py-2 bg-[#F5F2EB] border border-[#E6E2D8] rounded-lg hover:bg-[#E6E2D8] transition-colors"
                data-testid="brand-address-mobile"
              >
                <MapPin size={18} weight="fill" className="text-[#E85D04] flex-shrink-0 mt-0.5" />
                <span className="text-xs leading-tight">
                  <span className="font-semibold text-[#1A1A1A] block">{t('brand.address')}</span>
                  <span className="text-[#73706A]">{t('brand.addressFull')}</span>
                </span>
              </a>
              <button
                type="button"
                onClick={handleShare}
                data-testid="share-school-btn"
                className="w-full flex items-center justify-center gap-2 bg-white border-2 border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white font-semibold py-3 rounded-lg transition-colors"
              >
                <ShareNetwork size={18} weight="bold" />
                {t('home.share')}
              </button>
              <Link to="/tk-league" data-testid="tk-league-link">
                <button className="w-full flex items-center justify-center gap-2 bg-[#1A1A1A] hover:bg-[#333] text-white font-semibold py-3 rounded-lg transition-colors">
                  <span className="text-[#FFB703]">&#9733;</span>
                  {t('home.ctaTKLeague')} - {t('home.ctaTKLeagueDesc')}
                </button>
              </Link>
              <Link to="/aula-avulsa" data-testid="drop-in-link">
                <button className="w-full flex items-center justify-center gap-2 bg-[#0077B6] hover:bg-[#03045E] text-white font-semibold py-3 rounded-lg transition-colors mt-3">
                  {t('home.ctaDropIn')} - $25.00 ({t('dropIn.firstTrialTitle')} $15)
                </button>
              </Link>
              <Link to="/open-play" data-testid="open-play-link">
                <button className="w-full flex items-center justify-center gap-2 bg-[#2D6A4F] hover:bg-[#1B4332] text-white font-semibold py-3 rounded-lg transition-colors mt-3">
                  <span>&#9917;</span>
                  {i18n.language === 'en' ? 'Open Play — $12 students · $15 visitors' : 'Open Play — $12 aluno · $15 visitante'}
                </button>
              </Link>
              <Link to="/loja" data-testid="public-store-link">
                <button className="w-full flex items-center justify-center gap-2 bg-[#E85D04] hover:bg-[#DC2F02] text-white font-semibold py-3 rounded-lg transition-colors mt-3">
                  <span>&#128722;</span>
                  {t('home.ctaStore')} - {t('home.ctaStoreDesc')}
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
